import { MarkerClusterer } from '@googlemaps/markerclusterer';

import { createUserMarker } from './user-marker';

const VIEW_MODES = ['dealers', 'dealer', 'dealer_directions'];

/**
 * Wrapper class which provides interface for managing google maps.
 */
class DealerMap {

  /**
   * @type {google.maps.Map}
   */
  #map = null;
  /**
   * @type {Record<string, google.maps.Marker>}
   */
  #markers = {};
  /**
   * @type {MarkerClusterer}
   */
  #markerClusterer = null;
  /**
   * @type {google.maps.OverlayView}
   */
  #userMarker = null;
  /**
   * @type {google.maps.DirectionsService}
   */
  #directionsService = null;
  /**
   * @type {google.maps.DirectionsRenderer}
   */
  #directionsDisplay = null;
  #i18n = {
    clusterOfMarkers: 'Cluster of %n% markers',
  };

  #boundsChangedReason = null;
  #mode = null;
  #resizeListener = null;
  #resizeTimeout = null;
  #listeners = [];

  /**
   * Create new DealerMap.
   *
   * @param {object} options Map options.
   * @param {HTMLElement} options.element Html element id.
   * @param {google.maps.LatLngLiteral} [options.centerLocation] Initial center point.
   * @param {number} [options.zoom] Initial zoom level.
   * @param {GeolocationCoordinates} [options.userLocation] User location from browser.
   * @param {object[]} [options.dealers] Dealeres which marker is added to map.
   * @param {object} [options.i18n] Map localisations.
   */
  constructor(options = {}) {
    const triggerMapUpdate = (() => {
      const data = {
        reason: this.#boundsChangedReason,
        north: this.#map.getBounds().getNorthEast().lat(),
        east: this.#map.getBounds().getNorthEast().lng(),
        south: this.#map.getBounds().getSouthWest().lat(),
        west: this.#map.getBounds().getSouthWest().lng(),
        center: this.#map.getCenter().toJSON(),
        zoom: this.#map.getZoom(),
      };
      this.#boundsChangedReason = null;
      this.#triggerEvent('map_update', [data]);
    }).bind(this);

    const resizeTimeout = (() => {
      this.#resizeTimeout = null;
      triggerMapUpdate();
    }).bind(this);

    this.#resizeListener = (() => {
      if (this.#boundsChangedReason === null) {
        this.#boundsChangedReason = 'resize';
        this.#resizeTimeout = setTimeout(resizeTimeout, 300);
      }
    }).bind(this);
    window.addEventListener('resize', this.#resizeListener);

    const i18n = {};
    const i18nOverrides = options.i18n || {};
    for (let key in this.#i18n) {
      i18n[key] = typeof i18nOverrides[key] === 'string' ? i18nOverrides[key] : this.#i18n[key];
    }
    this.#i18n = i18n;
    this.#map = new window.google.maps.Map(options.element, {
      center: options.centerLocation || { lat: 60.1102325, lng: 24.8538795 },
      zoom: options.zoom || 15,
      minZoom: 3,
      maxZoom: 18,
    });
    this.#map.addListener('center_changed', () => {
      if (this.#boundsChangedReason === null) {
        this.#boundsChangedReason = 'center';
      }
    });
    this.#map.addListener('zoom_changed', () => {
      if (this.#boundsChangedReason === null) {
        this.#boundsChangedReason = 'zoom';
      }
    });
    this.#map.addListener('dragstart', () => {
      if (this.#boundsChangedReason === null) {
        this.#boundsChangedReason = 'drag';
      }
    });
    this.#map.addListener('idle', () => {
      if (this.#resizeTimeout) {
        clearTimeout(this.#resizeTimeout);
        this.#resizeTimeout = setTimeout(resizeTimeout, 300);
        return;
      }

      triggerMapUpdate();
    });
    const clusterer = this.#createDealerMarkerClusterer();
    clusterer.setMap(this.#map);
    this.#markerClusterer = clusterer;

    if (options.dealers) {
      this.setDealers(options.dealers);
    }
    if (options.userLocation) {
      this.setUserLocation(options.userLocation);
    }
  }

  /**
   * Get dealer map.
   *
   * @returns {google.maps.Map}
   */
  getMap() {
    return this.#map;
  }

  /**
   * Get current user location.
   *
   * @returns {google.maps.Marker | null}
   */
  getUserLocation() {
    return this.#userMarker || null;
  }

  /**
   * Sets user location marker on the map.
   *
   * @param {GeolocationCoordinates} location Coordinates received from browser `navigator.geolocation.getCurrentPosition`.
   */
  setUserLocation(location) {
    const latlng = {
      lat: location.lat,
      lng: location.lng
    };
    if (!this.#userMarker) {
      this.#userMarker = createUserMarker(latlng);
      if (this.#map) {
        this.#userMarker.setMap(this.#map);
      }
    }
    else {
      this.#userMarker.setPosition(latlng);
      this.#userMarker.show();
    }
  }

  /**
   * Removes user location marker.
   */
  removeUserLocation() {
    this.#userMarker?.hide();
  }

  #triggerEvent(name, data) {
    this.#listeners.filter(item => item.name === name).forEach(item => {
      item.callback.apply(null, data);
    });
  }

  /**
   * Add listener to map.
   *
   * @param {string} name map_update, viewmode, marker_click, marker_hover, marker_hoverout
   * @param {funciton} callback Event callback.
   */
  addListener(name, callback) {
    this.#listeners.push({ name, callback });
  }

  /**
   * Remove listener from map.
   *
   * @param {string} name map_update, viewmode, marker_click, marker_hover, marker_hoverout
   * @param {funciton} callback Event callback.
   */
  removeListener(name, callback) {
    this.#listeners = this.#listeners.filter(item => item.name !== name || item.callback !== callback);
  }

  /**
   * Create marker clusterer for dealer markers.
   *
   * @returns {MarkerClusterer}
   */
  #createDealerMarkerClusterer() {
    const i18n = this.#i18n;
    return new MarkerClusterer({
      minimumClusterSize: 2,
      algorithmOptions: {
        maxZoom: 13,
      },
      renderer: {
        render({ count, position }, stats, map) {
          const color = '#51b848';

          // Create svg url with fill color
          const svg = `
            <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="50" height="50">
            <circle cx="120" cy="120" opacity=".8" r="70" />
            <circle cx="120" cy="120" opacity=".55" r="90" />
            <circle cx="120" cy="120" opacity=".3" r="110" />
            <text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
            </svg>
          `;

          const title = i18n.clusterOfMarkers.split('%n%').join(count);
          const zIndex = Number(window.google.maps.Marker.MAX_ZINDEX) + count;

          const clusterOptions = {
            visible: count > 0,
            position,
            zIndex,
            title,
            icon: {
              url: `data:image/svg+xml;base64,${btoa(svg)}`,
              anchor: new window.google.maps.Point(25, 25),
            },
          };
          return new window.google.maps.Marker(clusterOptions);
        }
      },
    });
  }

  #createDealerMarker(dealer) {
    const markerTypes = {
      active: {
        iconUrl: dealer.marker.active,
        zIndex: dealer.marker.activeIndex,
      },
      hover: {
        iconUrl: dealer.marker.active,
        zIndex: dealer.marker.activeIndex,
      },
      default: {
        iconUrl: dealer.marker.inactive,
        zIndex: dealer.marker.inactiveIndex,
      }
    };
    const markerType = dealer.markerType || 'default';

    const marker = new window.google.maps.Marker({
      map: this.#map,
      visible: this.#mode === 'dealers',
      position: {
        lat: dealer.lat,
        lng: dealer.long,
      },
      icon: {
        url: markerTypes[markerType].iconUrl,
        size: new window.google.maps.Size(54, 60),
        scaledSize: new window.google.maps.Size(54, 60),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(25, 58),
      },
      zIndex: markerTypes[markerType].zIndex,
      title: [dealer.company_name, dealer.company_name_extra].filter(val => val).join('\n')
    });

    marker.uuid = dealer.uuid;
    marker.markerType = markerType;
    marker.markerTypes = markerTypes;

    marker.addListener('click', () => {
      this.#triggerEvent('marker_click', [marker.uuid]);
    });

    marker.setMarkerType = (type) => {
      marker.setIcon({
        url: marker.markerTypes[type].iconUrl,
        size: new window.google.maps.Size(54, 60),
        scaledSize: new window.google.maps.Size(54, 60),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(25, 58),
      });

      marker.setZIndex(marker.markerTypes[type].zIndex);
    };

    marker.addListener('mouseover', () => {
      if (marker.markerType !== 'active') {
        marker.setMarkerType('hover');
      }
      this.#triggerEvent('marker_hover', [marker.uuid]);
    });

    marker.addListener('mouseout', () => {
      if (marker.markerType !== 'active') {
        marker.setMarkerType('default');
      }
      this.#triggerEvent('marker_hoverout', [marker.uuid]);
    });

    return marker;
  }

  /**
   * Set available dealer markers.
   *
   * @param {object[]} dealers Array of dealer objects.
   */
  setMarkers(dealers) {
    const markers = {};
    let markersUpdated = false;

    dealers.forEach(dealer => {
      const markerObject = this.getMarker(dealer.uuid);

      // Marker exists already, add reference to new object
      if (markerObject) {
        markers[dealer.uuid] = markerObject;
        return;
      }

      const marker = this.#createDealerMarker(dealer);
      if (marker) {
        markers[dealer.uuid] = marker;
        this.#markerClusterer.addMarker(marker, true);
        markersUpdated = true;
      }
    });

    // Remove markers from map that are not available anymore
    for (let uuid in this.#markers) {
      if (!markers[uuid]) {
        const marker = this.#markers[uuid];
        marker.setMap(null);
        this.#markerClusterer.removeMarker(marker, true);
        markersUpdated = true;
      }
    }

    if (markersUpdated) {
      this.#markers = markers;
      this.#markerClusterer.render();
    }
  }

  /**
   * Get dealer marker.
   *
   * @param {string} uuid Dealer uuid.
   * @returns {google.maps.Marker|null} Marker instance or null if dealer marker does not exist.
   */
  getMarker(uuid) {
    return this.#markers[uuid] || null;
  }

  /**
   * Get all dealer markers.
   *
   * @returns {google.maps.Marker[]} Array of markers.
   */
  getMarkers() {
    return Object.values(this.#markers);
  }

  /**
   * Get current view mode.
   *
   * @returns {string}
   */
  getViewMode() {
    return this.#mode;
  }

  #setMarkerVisibility(uuids = []) {
    for (let uuid in this.#markers) {
      const visible = uuids.length === 0 || uuids.includes(uuid);
      const marker = this.#markers[uuid];
      marker.setVisible(visible);
    }
  }

  /**
   * Get current map bounds.
   *
   * @returns {google.maps.LatLngBoundsLiteral}
   */
  getBounds() {
    return this.#map.getBounds().toJSON();
  }

  /**
   * Set map bounds.
   *
   * @param {google.maps.LatLngBoundsLiteral} bounds New bounds.
   */
  setBounds(bounds) {
    this.#map.fitBounds(bounds, 0);
  }

  /**
   * Fit given dealers in map bounds.
   *
   * @param {string[]} dealerUuids Array of dealer uuids. Note! Dealer marker must exist.
   * @param {boolean} includeUserInBounds Whether user location is included in map bounds.
   */
  fitBounds(dealerUuids, includeUserInBounds = true) {
    const latLngBounds = new window.google.maps.LatLngBounds();

    // Include user position
    if (includeUserInBounds && this.#userMarker) {
      latLngBounds.extend(this.#userMarker.getPosition());
    }

    dealerUuids.forEach(uuid => {
      const marker = this.#markers[uuid];
      if (marker) {
        latLngBounds.extend(marker.getPosition());
      }
    });

    if (latLngBounds.isEmpty()) {
      return;
    }

    if (this.#boundsChangedReason === null) {
      this.#boundsChangedReason = 'fitbounds';
    }
    this.#map.fitBounds(latLngBounds, 30);
  }

  /**
   * Set map view mode.
   *
   * @param {string} mode View mode to set
   * @param {object} options View mode options
   * @param {string} [options.dealerUuid] Dealer uuid if view mode is dealer or dealer_directions
   * @param {string[]} [options.dealerUuids] List of dealers that are included in map view
   * @param {string[]} [options.fitBoundsDealerUuids] List of dealer uuids that are included in bounds. Defaults to all dealers.
   */
  setViewMode(mode, options = {}) {
    if (!VIEW_MODES.includes(mode)) {
      console.warn('Invalid view mode: ', mode);
      return;
    }

    if (mode === 'dealer_directions' && !this.#userMarker) {
      console.error('Directions view mode cannot be used when user location is unknown');
      return;
    }

    if (this.#directionsDisplay) {
      this.#directionsDisplay.setMap(null);
    }

    const oldMode = this.getViewMode();
    if (mode === 'dealers') {
      const dealerUuids = options.dealerUuids || [];
      this.#setMarkerVisibility(dealerUuids);
      const fitBoundsDealers = options.fitBoundsDealerUuids || dealerUuids;
      if (fitBoundsDealers.length > 0) {
        this.fitBounds(fitBoundsDealers || dealerUuids, true);
      }
    }
    else if (mode === 'dealer') {
      const dealerUuid = options.dealerUuid;
      this.#setMarkerVisibility([dealerUuid]);
      this.fitBounds([dealerUuid], false);
      this.#map.setZoom(15);
    }
    else if (mode === 'dealer_directions') {
      const dealerUuid = options.dealerUuid;
      const marker = this.getMarker(dealerUuid);
      this.#setMarkerVisibility([dealerUuid]);
      this.fitBounds([dealerUuid], true);
      this.#markerClusterer.render();

      if (!this.#directionsService) {
        this.#directionsService = new window.google.maps.DirectionsService();
        this.#directionsDisplay = new window.google.maps.DirectionsRenderer();
      }

      this.#directionsService.route({
        origin: this.#userMarker.getPosition(),
        destination: marker.getPosition(),
        travelMode: 'DRIVING'
      }).then((result) => {
        this.#directionsDisplay.setDirections(result);
        this.#directionsDisplay.setMap(this.#map);
      }).catch(() => {
        this.#directionsDisplay.setMap(null);
      });
    }

    this.#mode = mode;
    this.#triggerEvent('viewmode', [mode, oldMode]);
  }

  /**
   * Destroys this map instance and removes map from dom.
   */
  destroy() {
    if (!this.#map) {
      return;
    }
    this.#map.unbindAll();
    this.#markerClusterer.unbindAll();
    this.#markerClusterer.clearMarkers();
    this.#markerClusterer.setMap(null);
    for (let id in this.#markers) {
      this.#markers[id].setMap(null);
      this.#markers[id].unbindAll();
    }
    this.#userMarker?.unbindAll();
    this.#userMarker?.setMap(null);
    this.#directionsDisplay?.unbindAll();
    this.#directionsDisplay?.setMap(null);
    this.#map.getDiv().innerHTML = '';

    this.#directionsDisplay = null;
    this.#directionsService = null;
    this.#markerClusterer = null;
    this.#map = null;
    this.#markers = [];
    this.#userMarker = null;
    this.#listeners = [];

    window.removeEventListener('resize', this.#resizeListener);
    this.#resizeListener = null;
    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
      this.#resizeTimeout = null;
    }
  }

}

export default DealerMap;
